"use client";
import { styled } from "@mui/material/styles";
import { ColorGray600, ColorGray300, ColorGray400, SpaceMd } from "@nxt-costco-com/forge-design-tokens";
import { CostcoBox } from "../../../Util/CostcoBox.js";
import GenericImageHandler from "../../../Util/GenericImageHandler.js";
const CommerceDisplayBorderRadius = "10px";
const StyledInGridBannerMediaContainer = styled("div")`
    width: 100%;
    height: auto;
    a {
        display: block;
        width: 100%;
    }
    a > img,
    img {
        max-width: 100%;
        height: auto;
        object-fit: cover;
        @media screen and (-webkit-min-device-pixel-ratio: 0) and (max-width: 768px) {
            /* Styles generally targeting iPhone-sized displays in WebKit browsers */
            width: 100%;
        }
    }
`;
const CommerceBannerSizingMap = {
  flagship: {
    containerHeight: "200px",
    height: "200px",
    maxWidth: "576px",
    minWidth: "384px"
  },
  showcase: {
    containerHeight: "500px",
    height: "500px",
    maxWidth: "310px",
    minWidth: "180px"
  }
};
const skeletonStyle = {
  borderRadius: CommerceDisplayBorderRadius,
  height: { lg: "249px", md: "736px", xs: "343px" },
  width: { lg: "976px", md: "736px", xs: "343px" }
};
const VideoSpotlightMainContainer = styled(CostcoBox, {
  shouldForwardProp: (prop) => prop !== "isMobile" && prop !== "isSmall"
})(({ isMobile, isSmall }) => ({
  border: `1px solid`,
  borderColor: ColorGray600,
  borderRadius: CommerceDisplayBorderRadius,
  display: "flex",
  flex: 1,
  flexDirection: !isMobile ? "row" : "column",
  maxWidth: isSmall ? "640px" : void 0
}));
const VideoSpotlightContainer = styled(CostcoBox, {
  shouldForwardProp: (prop) => prop !== "isMobile"
})(() => ({
  border: `1px solid`,
  borderColor: ColorGray600,
  borderRadius: CommerceDisplayBorderRadius,
  display: "flex",
  flex: 1,
  flexDirection: "column"
}));
const ButterflyDisplayContainer = styled(CostcoBox, {
  shouldForwardProp: (prop) => prop !== "isMobile" && prop !== "borderColor"
})(
  ({ isMobile, borderColor }) => ({
    border: `1px solid`,
    borderColor,
    borderRadius: CommerceDisplayBorderRadius,
    display: "flex",
    flex: 1,
    flexDirection: isMobile ? "column" : "row"
  })
);
const ButterflyDisplayImage = styled(GenericImageHandler, {
  shouldForwardProp: (prop) => prop !== "isMobile" && prop !== "isFlagship"
})(({ isMobile }) => {
  const bannerContainerMaxWidth = "310px";
  return {
    "@keyframes loading": {
      "0%": {
        backgroundColor: ColorGray400
      },
      "100%": {
        backgroundColor: ColorGray300
      }
    },
    animation: "loading 2s linear infinite alternate",
    backgroundPosition: "top center",
    objectFit: "cover",
    objectPosition: "top center",
    width: !isMobile ? bannerContainerMaxWidth : "100%"
  };
});
const ButterflyDisplayCarouselContainer = styled(CostcoBox, {
  shouldForwardProp: (prop) => prop !== "isMobile"
})(({ isMobile }) => {
  return {
    flex: 1,
    // The calc adds width that the carousel removes using negative margin
    width: isMobile ? `100%` : "50%"
  };
});
const CommerceDisplayContainer = styled(CostcoBox, {
  shouldForwardProp: (prop) => prop !== "isMobile" && prop !== "borderColor"
})(
  ({ isMobile, borderColor }) => ({
    alignItems: "center",
    border: `1px solid`,
    borderColor,
    borderRadius: CommerceDisplayBorderRadius,
    display: "flex",
    flex: 1,
    flexDirection: isMobile ? "column" : "row"
  })
);
const CommerceDisplayBannerContainer = styled(CostcoBox, {
  shouldForwardProp: (prop) => prop !== "isMobile" && prop !== "isDesktop" && prop !== "isFlagship"
})(({
  isMobile,
  isFlagship
}) => {
  let bannerBorderStyles = {};
  if (isMobile) {
    bannerBorderStyles = {
      borderTopLeftRadius: CommerceDisplayBorderRadius,
      borderTopRightRadius: CommerceDisplayBorderRadius
    };
  }
  let bannerMinWidth;
  let bannerMaxWidth;
  let maxContainerHeight;
  if (isFlagship) {
    bannerMinWidth = CommerceBannerSizingMap.flagship.minWidth;
    bannerMaxWidth = CommerceBannerSizingMap.flagship.maxWidth;
    maxContainerHeight = CommerceBannerSizingMap.flagship.containerHeight;
  } else {
    bannerMinWidth = CommerceBannerSizingMap.showcase.minWidth;
    bannerMaxWidth = CommerceBannerSizingMap.showcase.maxWidth;
    maxContainerHeight = CommerceBannerSizingMap.showcase.containerHeight;
  }
  return {
    alignItems: "center",
    display: "flex",
    flex: 1,
    justifyContent: "center",
    maxHeight: maxContainerHeight,
    maxWidth: !isMobile ? bannerMaxWidth : "100%",
    minWidth: !isMobile ? bannerMinWidth : void 0,
    overflow: "hidden",
    width: isMobile ? "100%" : void 0,
    ...bannerBorderStyles
  };
});
const CommerceDisplayImage = styled(GenericImageHandler, {
  shouldForwardProp: (prop) => prop !== "isMobile" && prop !== "isFlagship"
})(({ isMobile, isFlagship }) => {
  let bannerHeight;
  let bannerContainerMaxWidth;
  if (isFlagship) {
    bannerHeight = CommerceBannerSizingMap.flagship.height;
    bannerContainerMaxWidth = CommerceBannerSizingMap.flagship.maxWidth;
  } else {
    bannerHeight = CommerceBannerSizingMap.showcase.height;
    bannerContainerMaxWidth = CommerceBannerSizingMap.showcase.maxWidth;
  }
  return {
    "@keyframes loading": {
      "0%": {
        backgroundColor: ColorGray400
      },
      "100%": {
        backgroundColor: ColorGray300
      }
    },
    animation: "loading 2s linear infinite alternate",
    backgroundPosition: "top center",
    height: !isMobile ? bannerHeight : void 0,
    minHeight: !isMobile ? bannerHeight : "50px",
    minWidth: !isMobile ? bannerContainerMaxWidth : void 0,
    objectFit: "fill",
    objectPosition: "top center",
    width: !isMobile ? bannerContainerMaxWidth : "100%"
  };
});
const CommerceDisplayCarouselContainer = styled(CostcoBox, {
  shouldForwardProp: (prop) => prop !== "isMobile" && prop !== "isFlagship"
})(({ isMobile, isFlagship }) => {
  let maxCarouselWidth = "60%";
  if (isMobile) {
    maxCarouselWidth = "100%";
  }
  return {
    flex: 1,
    maxWidth: isFlagship ? maxCarouselWidth : void 0,
    minWidth: isMobile ? "90%" : void 0,
    padding: `0 ${SpaceMd}`
  };
});
export {
  ButterflyDisplayCarouselContainer,
  ButterflyDisplayContainer,
  ButterflyDisplayImage,
  CommerceBannerSizingMap,
  CommerceDisplayBannerContainer,
  CommerceDisplayBorderRadius,
  CommerceDisplayCarouselContainer,
  CommerceDisplayContainer,
  CommerceDisplayImage,
  StyledInGridBannerMediaContainer,
  VideoSpotlightContainer,
  VideoSpotlightMainContainer,
  skeletonStyle
};
